import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Col, Image, Row, Space } from 'antd';
import React, { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountAPI } from 'api/account';
import SmallJumpBigLeap from 'assets/small_jump_big_leap.png';
import RocketlaneLogoSvg from 'assets/svgs/rocketlane_logo_full.svg';
import { PageLoading } from 'components/atoms/loading';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { ProductiveHeading03 } from 'components/styles';
import { useQueryParams } from 'hooks/use-query-params';
import { useDismissBootSkeleton } from 'utils/dismiss-boot-skeleton';
import { getTokenMeta } from 'utils/token';
import { AccountSetupForm } from './form';
import { MultipleAccounts } from './multiple-accounts';
const TokenError = /*#__PURE__*/ React.lazy(()=>import('components/organisms/token-error/token-error').then((d)=>({
            default: d.TokenError
        })));
const StyledRow = styled(Row)`
  background-color: var(--gray-10);
  height: 100vh;
`;
const Left = styled(Col)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const LeftTop = styled.div`
  padding: var(--spacing-08) var(--spacing-10);
`;
const LeftBottom = styled.div`
  height: 50%;
  background-image: url(${SmallJumpBigLeap});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`;
const Right = styled(Col)`
  background-color: var(--white-100);
`;
const FlexBox = styled.div`
  display: flex;
  justify-content: center;
  padding-top: var(--spacing-10);
  height: 100%;
  overflow-y: auto;
`;
const Container = styled.div`
  width: 368px;
`;
const BaseHeading = styled.div`
  ${ProductiveHeading03};
  width: 80%;
`;
const PrimaryHeading = styled(BaseHeading)`
  font-weight: var(--font-weight-bold);
`;
const SecondaryHeading = BaseHeading;
var NextPageFlag = /*#__PURE__*/ function(NextPageFlag) {
    NextPageFlag["SingleAccount"] = "single";
    NextPageFlag["MultipleAccount"] = "multiple";
    return NextPageFlag;
}(NextPageFlag || {});
export const AccountActivatePage = ()=>{
    var _params_get, _params_get1, _params_get2, _params_get3, _params_get4, _params_get5;
    // Public route — dismiss the static #rl-boot-skeleton overlay from
    // public/index.html so it doesn't bleed through this page (or the
    // <TokenError /> branch when token is invalid). See Login.
    useDismissBootSkeleton();
    const params = useQueryParams();
    const token = (_params_get = params.get('token')) !== null && _params_get !== void 0 ? _params_get : '';
    const credential = (_params_get1 = params.get('credential')) !== null && _params_get1 !== void 0 ? _params_get1 : '';
    const domainCheckToken = (_params_get2 = params.get('signup_token')) !== null && _params_get2 !== void 0 ? _params_get2 : '';
    const { decoded, isExpired } = getTokenMeta(token || credential || '');
    const nextPageFlag = (_params_get3 = params.get('next_page')) !== null && _params_get3 !== void 0 ? _params_get3 : '';
    const domain = (_params_get4 = params.get('domain')) !== null && _params_get4 !== void 0 ? _params_get4 : '';
    const loginToken = (_params_get5 = params.get('login_token')) !== null && _params_get5 !== void 0 ? _params_get5 : '';
    if (!decoded) {
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
            children: /*#__PURE__*/ _jsx(TokenError, {
                status: TokenErrorStatus.Invalid
            })
        });
    }
    if (nextPageFlag && nextPageFlag === "single" && domain) {
        window.location.replace(`https://${domain}?token=${loginToken}&overrideAuth=true`);
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    if (nextPageFlag && nextPageFlag === "multiple") {
        return /*#__PURE__*/ _jsx(MultipleAccounts, {
            email: decoded.email ? decoded.email : ''
        });
    }
    if (isExpired) {
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
            children: /*#__PURE__*/ _jsx(TokenError, {
                email: decoded.emailId,
                status: TokenErrorStatus.Expired,
                resendEmail: ()=>AccountAPI.resendAccountActivation()
            })
        });
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs(StyledRow, {
            gutter: 0,
            wrap: false,
            children: [
                /*#__PURE__*/ _jsxs(Left, {
                    flex: "auto",
                    children: [
                        /*#__PURE__*/ _jsx(LeftTop, {
                            children: /*#__PURE__*/ _jsxs(Space, {
                                direction: "vertical",
                                size: 84,
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        children: /*#__PURE__*/ _jsx(Image, {
                                            width: 160,
                                            src: RocketlaneLogoSvg,
                                            alt: "Rocketlane",
                                            preview: false
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(Space, {
                                        direction: "vertical",
                                        size: 16,
                                        children: [
                                            /*#__PURE__*/ _jsx(PrimaryHeading, {
                                                children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                    id: "account_setup.title"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(SecondaryHeading, {
                                                children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                    id: "account_setup.message"
                                                })
                                            })
                                        ]
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(LeftBottom, {})
                    ]
                }),
                /*#__PURE__*/ _jsx(Right, {
                    flex: "600px",
                    children: /*#__PURE__*/ _jsx(FlexBox, {
                        children: /*#__PURE__*/ _jsx(Container, {
                            children: /*#__PURE__*/ _jsxs(Space, {
                                direction: "vertical",
                                size: 24,
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "account_setup.signing_up_as",
                                            values: {
                                                b: (chunks)=>/*#__PURE__*/ _jsx("b", {
                                                        children: chunks
                                                    }),
                                                emailId: credential ? decoded.email ? decoded.email : '' : decoded.emailId
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(AccountSetupForm, {
                                        token: token || credential,
                                        emailId: credential ? decoded.email ? decoded.email : '' : decoded.emailId,
                                        throughGoogleAuth: !!credential,
                                        name: credential && decoded.name,
                                        domainCheckToken: credential && domainCheckToken
                                    })
                                ]
                            })
                        })
                    })
                })
            ]
        })
    });
};
